import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Empty, Input, Select, Table, Tag, Tooltip, message } from "antd";

import {
  addCarrierAlias,
  getCarrierAliases,
  getUnresolvedPmsCarriers,
  markCarrierReviewed,
  removeCarrierAlias,
} from "./controller";

const SOURCE_LABEL = {
  seed: "from office mappings",
  admin: "added here",
  office_created: "office added the carrier",
  llm_confirmed: "AI suggestion, confirmed",
};

function errorOf(res, fallback) {
  return (res && res.json && (res.json.error || res.json.detail)) || fallback;
}

/**
 * The carrier drawer's "Names" tab: the other names offices' PMSs use for this
 * carrier ("ROYAL BANK INSURANCE" → RBC Insurance). The PMS import resolves every
 * name here for every office without asking anyone. Also where a carrier an
 * office added gets reviewed — carriers are shared by every practice.
 */
export function CarrierNamesTab({ carrier, onChanged }) {
  const [aliases, setAliases] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  // The drawer holds a snapshot of the row; remember the review here.
  const [reviewed, setReviewed] = useState(false);
  const carrierId = carrier && carrier.carrier_id;

  const load = useCallback(async function () {
    if (!carrierId) return;
    const res = await getCarrierAliases(carrierId);
    setAliases(res.status < 400 && Array.isArray(res.json) ? res.json : []);
  }, [carrierId]);

  useEffect(function () { load(); }, [load]);

  async function add() {
    const name = draft.trim();
    if (!name) return;
    setBusy(true);
    const res = await addCarrierAlias(name, carrierId);
    setBusy(false);
    if (res.status === 201) {
      setDraft("");
      load();
      return;
    }
    const other = res.json && res.json.alias;
    message.error(other && other.provider_name
      ? "“" + name + "” already means " + other.provider_name + "."
      : errorOf(res, "Couldn't add that name."));
  }

  async function remove(alias) {
    const res = await removeCarrierAlias(alias.id);
    if (res.status < 400) load();
    else message.error(errorOf(res, "Couldn't remove that name."));
  }

  async function review() {
    setBusy(true);
    const res = await markCarrierReviewed(carrierId);
    setBusy(false);
    if (res.status < 400) {
      setReviewed(true);
      message.success("Marked as reviewed");
      if (onChanged) onChanged();
    } else {
      message.error(errorOf(res, "Couldn't mark it reviewed."));
    }
  }

  return (
    <div className="ck-names">
      {carrier && carrier.needs_review && !reviewed ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={"Added by " + (carrier.added_from_office_name || "an office") + " from a PMS carrier we didn't have"}
          description="Every practice now sees it. Check the name and phone on the Carrier tab — merge it into an existing carrier if it's a duplicate — then mark it reviewed."
          action={<Button size="small" onClick={review} loading={busy}>Mark reviewed</Button>}
        />
      ) : null}

      <p className="ck-muted" style={{ marginTop: 0 }}>
        Other names offices' practice management systems use for <strong>{carrier && carrier.carrier_name}</strong>.
        A policy imported under any of these names is filed under this carrier automatically, for every practice.
      </p>

      {aliases === null ? null : aliases.length ? (
        <div className="ck-alias-list" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {aliases.map(function (a) {
            return (
              <Tooltip key={a.id} title={SOURCE_LABEL[a.source] || a.source}>
                <Tag closable onClose={function (e) { e.preventDefault(); remove(a); }}>{a.name}</Tag>
              </Tooltip>
            );
          })}
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No other names yet" style={{ marginBottom: 16 }} />
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Input
          value={draft}
          placeholder="e.g. ROYAL BANK INSURANCE"
          onChange={function (e) { setDraft(e.target.value); }}
          onPressEnter={add}
        />
        <Button type="primary" onClick={add} loading={busy} disabled={!draft.trim()}>Add name</Button>
      </div>
    </div>
  );
}

const KIND_TAG = {
  placeholder: { color: "default", label: "Not a carrier name" },
  network: { color: "orange", label: "Claims network" },
};

/**
 * The page's "PMS carriers" tab: names offices' PMSs sent that nothing could
 * match yet. Naming one here (it becomes an alias) files every policy with that
 * name under the carrier on its next import, at every practice.
 */
export function UnresolvedPmsCarriersPanel({ carriers, onChanged }) {
  const [rows, setRows] = useState(null);
  const [choice, setChoice] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async function () {
    const res = await getUnresolvedPmsCarriers();
    setRows(res.status < 400 && Array.isArray(res.json) ? res.json : []);
  }, []);

  useEffect(function () { load(); }, [load]);

  async function name(row, carrierId) {
    setBusyKey(row.key);
    const res = await addCarrierAlias(row.name, carrierId);
    setBusyKey(null);
    if (res.status === 201) {
      message.success(carrierId
        ? "“" + row.name + "” will be filed under " + ((res.json && res.json.provider_name) || "that carrier") + " from the next import."
        : "“" + row.name + "” will never be matched on its own.");
      load();
      if (onChanged) onChanged();
    } else {
      message.error(errorOf(res, "Couldn't save that."));
    }
  }

  const options = (carriers || [])
    .map(function (c) { return { value: c.carrier_id, label: c.carrier_name }; })
    .sort(function (a, b) { return String(a.label).localeCompare(String(b.label)); });

  const columns = [
    {
      title: "What the PMS calls it",
      key: "name",
      width: "30%",
      render: function (_, r) {
        const kind = KIND_TAG[r.kind];
        return (
          <div>
            <div className="ck-carrier">{r.name}</div>
            {kind ? <Tag color={kind.color} style={{ marginTop: 4 }}>{kind.label}</Tag> : null}
          </div>
        );
      },
    },
    {
      title: "Where",
      key: "offices",
      width: "22%",
      render: function (_, r) {
        const names = r.offices.map(function (o) { return o.name; }).join(", ");
        return (
          <Tooltip title={names}>
            <span className="ck-muted">
              {r.offices.length === 1 ? r.offices[0].name : r.offices.length + " practices"} · {r.mappings} plan{r.mappings === 1 ? "" : "s"}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "AI suggestion",
      key: "suggested",
      width: "18%",
      render: function (_, r) {
        return r.suggested
          ? <Tooltip title={r.suggested.reason}><span>{r.suggested.name}</span></Tooltip>
          : <span className="ck-muted">—</span>;
      },
    },
    {
      title: "File it under",
      key: "action",
      render: function (_, r) {
        if (r.kind === "placeholder") {
          return <span className="ck-muted">Offices choose per patient</span>;
        }
        const picked = choice[r.key] || (r.suggested ? r.suggested.id : undefined);
        return (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Choose a carrier"
              value={picked}
              options={options}
              style={{ minWidth: 200, flex: 1 }}
              onChange={function (v) { setChoice(Object.assign({}, choice, { [r.key]: v })); }}
            />
            <Button type="primary" size="small" disabled={!picked} loading={busyKey === r.key}
                    onClick={function () { name(r, picked); }}>
              Save
            </Button>
            {r.kind === "network" ? null : (
              <Tooltip title="A claims network or a name shared by different plans: never match it automatically">
                <Button size="small" onClick={function () { name(r, null); }}>Never auto-match</Button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="crm-table-card">
      <Table
        rowKey="key"
        loading={rows === null}
        columns={columns}
        dataSource={rows || []}
        pagination={false}
        locale={{ emptyText: "Every PMS carrier name is matched." }}
      />
    </div>
  );
}
